/**
 * Small status pills shared across the dashboard: {@link StateBadge} for a Check's rolled-up
 * up/down Check_State, and {@link StatusBadge} for a single result's passing/degraded/failing
 * Result_Status. Colours are driven by CSS classes in `styles.css`.
 */

import type { CheckState, EntityOrigin, ResultStatus } from '@soroush/core';

import type { DraftCheckState, InvitationState } from '../api/types';

/**
 * A Check's rolled-up up/down state (Req 9.2, 9.3), or "Never run" when it has no results yet.
 *
 * The never-run case is not cosmetic. The Check_State fold starts at `up` — a Check is up until enough
 * consecutive failures mark it down (9.2) — and the read path synthesises that initial fold for a Check
 * with no persisted state at all. So a Check that has **never executed** arrives as `state: 'up'` with
 * `hasResults: false`, and rendering it green says the one thing a monitoring tool must never say
 * wrongly: that something has been checked and is fine.
 *
 * `hasResults` is where the truth lives, and it was being ignored here. Absorbed into this badge rather
 * than handled at each call site, for the reason {@link OriginBadge} absorbs its own defaulting: both
 * render sites then get it from one edit, and the two cannot disagree.
 *
 * `CheckState` has no `unknown` member, so this deliberately does not invent one on the wire — the
 * distinction is drawn at the point of display, where it belongs, and the domain fold stays untouched.
 */
export function StateBadge({
  state,
  hasResults,
}: {
  state: CheckState;
  /** Whether any result has ever been folded. `false` outranks `state` — see above. */
  hasResults?: boolean;
}): JSX.Element {
  if (hasResults === false) {
    return (
      <span className="state-badge state-never-run" title="No result has been recorded yet">
        Never run
      </span>
    );
  }
  return <span className={`state-badge state-${state}`}>{state === 'up' ? 'Up' : 'Down'}</span>;
}

const STATUS_LABEL: Record<ResultStatus, string> = {
  passing: 'Passing',
  degraded: 'Degraded',
  failing: 'Failing',
};

/** A single result's passing/degraded/failing classification (Req 9.1). */
export function StatusBadge({ status }: { status: ResultStatus }): JSX.Element {
  return <span className={`status-badge status-${status}`}>{STATUS_LABEL[status]}</span>;
}

/**
 * The label for each Origin the platform declares (Feature: terraform-provider, task 8.1; Req 12.1,
 * 12.2).
 *
 * `Record<EntityOrigin, string>` and not a partial or an index signature, deliberately: the map is
 * **exhaustive by type**, so the next value added to `EntityOrigin` fails this file to compile rather
 * than shipping a badge that quietly mislabels it. That is the whole reason the previous
 * `manifest ? 'Manifest' : 'API'` ternary had to go — it was total over the old two-valued vocabulary
 * and silently wrong the moment a third value existed, calling every `terraform` entity an API one.
 */
const ORIGIN_LABELS: Record<EntityOrigin, string> = {
  manifest: 'Manifest',
  api: 'API',
  terraform: 'Terraform',
};

/**
 * Which write path manages an entity (granular-crud-api Req 6.9, 11.8; terraform-provider Req 12.1).
 *
 * An absent `origin` reads as `manifest` — the platform's read-boundary default for entities persisted
 * before the attribute existed — so the badge never shows an entity as unmanaged (Req 12.2).
 *
 * The unknown-value fallback is unreachable through the type and load-bearing anyway (Req 12.3). The
 * compile-time guarantee above binds this repository at build time; it says nothing about the JSON a
 * **deployed** platform sends to an already-served SPA. Deploy order makes that concrete: the platform
 * ships first, so a fourth Origin can exist on the wire while a cached bundle still knows three labels.
 * Falling back to the value's own text makes that visible — mislabelling it, or rendering an empty pill,
 * would not.
 */
export function OriginBadge({ origin }: { origin?: EntityOrigin }): JSX.Element {
  // `resolved`, not the raw `origin`, in both positions: it is what keeps `undefined` rendering exactly
  // as it does today — the `Manifest` label *and* the `origin-manifest` class (Req 12.2) — rather than
  // an `origin-undefined` class no stylesheet matches. For any defined value the two are identical.
  const resolved: EntityOrigin = origin ?? 'manifest';
  // `Object.hasOwn`, not a bare `ORIGIN_LABELS[resolved] ?? resolved`, for the same reason
  // `api/writes.ts` indexes its route table through a `Map` and `generation/fetcher.ts` guards its
  // header reads: `origin` is **wire input**, and a bare object-literal lookup inherits
  // `Object.prototype`. A value of `toString`, `valueOf`, `constructor` or `__proto__` then resolves to
  // a prototype member instead of missing, so `??` never fires and React is handed a function or an
  // object — which it renders as nothing. That is the empty pill the paragraph above says must never
  // happen, produced by the very fallback meant to prevent it. Own-property check or nothing; do not
  // simplify this back to `??`.
  const label = Object.hasOwn(ORIGIN_LABELS, resolved) ? ORIGIN_LABELS[resolved] : resolved;
  return <span className={`origin-badge origin-${resolved}`}>{label}</span>;
}

const DRAFT_STATE_LABEL: Record<DraftCheckState, string> = {
  pending: 'Pending',
  accepted: 'Accepted',
  discarded: 'Discarded',
};

/**
 * A Draft_Check's review state — pending, accepted, or discarded (Req 9.8). Distinct from
 * {@link StateBadge}, which renders a *Check's* up/down Check_State: a draft is not yet a Check and
 * has no health, only a position in the review lifecycle.
 */
export function DraftStateBadge({ state }: { state: DraftCheckState }): JSX.Element {
  return <span className={`draft-badge draft-${state}`}>{DRAFT_STATE_LABEL[state]}</span>;
}

/**
 * An invitation's status *as displayed*, which is the wire {@link InvitationState} plus `expired`.
 *
 * `expired` is not a wire state: an invitation past its `expiresAtEpoch` is still `pending` on the
 * wire, so expiry is derived from the timestamp (Req 5.5). The derivation belongs to the caller,
 * which knows what "now" is; this type is the widened vocabulary the badge accepts.
 */
export type InvitationDisplayStatus = InvitationState | 'expired';

const INVITATION_STATUS_LABEL: Record<InvitationDisplayStatus, string> = {
  pending: 'Pending',
  accepted: 'Accepted',
  revoked: 'Revoked',
  expired: 'Expired',
};

/** Where a pending invitation stands (Req 5.2, 5.5). */
export function InvitationStatusBadge({ status }: { status: InvitationDisplayStatus }): JSX.Element {
  return (
    <span className={`invite-badge invite-${status}`}>{INVITATION_STATUS_LABEL[status]}</span>
  );
}
